import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { HelpCircle, Clock, CheckCircle, XCircle, FileText, MessageSquare, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelfHelpPortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    status: 'pending',
    form_data: {},
    rm_notes: '',
    hr_notes: '',
    admin_notes: ''
  });

  const categories = [
    'Salary Query',
    'Leave Query',
    'IT Support',
    'HR Support',
    'Reimbursement',
    'Document Request',
    'Other'
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'pending_rm', label: 'Pending RM' },
    { value: 'pending_hr', label: 'Pending HR' },
    { value: 'pending_payroll', label: 'Pending Payroll' },
    { value: 'pending_admin', label: 'Pending Admin' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' }
  ];

  useEffect(() => {
    fetchRequests();
  }, [impersonatedUser]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/self-help/${impersonatedUser.employee_id}`,
        config
      );
      
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching self-help data:', error);
      toast.error('Unable to fetch self-help data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: Clock, label: 'Pending' },
      'pending_rm': { bg: 'bg-orange-50', text: 'text-orange-700', icon: Clock, label: 'Pending RM' },
      'pending_hr': { bg: 'bg-purple-50', text: 'text-purple-700', icon: Clock, label: 'Pending HR' },
      'pending_payroll': { bg: 'bg-blue-50', text: 'text-blue-700', icon: Clock, label: 'Pending Payroll' },
      'pending_admin': { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: Clock, label: 'Pending Admin' },
      'completed': { bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle, label: 'Completed' },
      'rejected': { bg: 'bg-red-50', text: 'text-red-700', icon: XCircle, label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    const IconComponent = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.bg} ${config.text}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleOpenCreate = () => {
    setEditingRequest(null);
    setFormData({
      category: '',
      status: 'pending',
      form_data: { description: '' },
      rm_notes: '',
      hr_notes: '',
      admin_notes: ''
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (request) => {
    setEditingRequest(request);
    setFormData({
      category: request.category || '',
      status: request.status || 'pending',
      form_data: request.form_data || { description: '' },
      rm_notes: request.rm_notes || '',
      hr_notes: request.hr_notes || '',
      admin_notes: request.admin_notes || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        portal: 'self_help',
        employee_id: impersonatedUser.employee_id,
        data: {
          ...formData,
          employee_id: impersonatedUser.employee_id,
          employee_name: impersonatedUser.name,
          created_at: editingRequest?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      };

      if (editingRequest) {
        await axios.put(`${API}/superadmin/entries/update`, {
          ...payload,
          record_id: editingRequest.id
        }, config);
        toast.success('Request updated successfully');
      } else {
        await axios.post(`${API}/superadmin/entries/create`, payload, config);
        toast.success('Request created successfully');
      }

      setDialogOpen(false);
      fetchRequests();
    } catch (error) {
      console.error('Error saving request:', error);
      toast.error(error.response?.data?.detail || 'Failed to save request');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (request) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(`${API}/superadmin/entries/delete`, {
        ...config,
        data: {
          portal: 'self_help',
          employee_id: impersonatedUser.employee_id,
          record_id: request.id
        }
      });

      toast.success('Request deleted successfully');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleComplete = async (request) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(`${API}/superadmin/entries/update`, {
        portal: 'self_help',
        employee_id: impersonatedUser.employee_id,
        record_id: request.id,
        data: { ...request, status: 'completed' }
      }, config);

      toast.success('Request marked as completed');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="self-help-portal-embed">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-purple-600" />
              Self Help Requests for {impersonatedUser.name} ({requests.length})
            </CardTitle>
            <Button onClick={handleOpenCreate} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-1" /> Add Request
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No self-help requests found</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {requests.map((request, idx) => (
                <div 
                  key={request.id || idx} 
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-gray-900">{request.category}</h4>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(request.status)}
                      <div className="flex gap-1">
                        {request.status !== 'completed' && (
                          <Button size="sm" variant="ghost" onClick={() => handleComplete(request)} title="Mark Complete">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(request)}>
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(request)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">
                    Submitted: {formatDate(request.created_at)}
                  </p>
                  
                  {/* Form Data Preview */}
                  {request.form_data && (
                    <div className="text-sm text-gray-600 space-y-1">
                      {Object.entries(request.form_data).slice(0, 3).map(([key, value]) => (
                        value && key !== 'screenshot' && (
                          <p key={key}>
                            <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span> {
                              typeof value === 'string' ? value.substring(0, 100) + (value.length > 100 ? '...' : '') : String(value)
                            }
                          </p>
                        )
                      ))}
                    </div>
                  )}
                  
                  {/* Notes from approvers */}
                  {(request.rm_notes || request.hr_notes || request.admin_notes) && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Approval Notes:
                      </p>
                      {request.rm_notes && (
                        <p className="text-xs bg-blue-50 p-2 rounded mb-1">
                          <span className="font-medium">RM:</span> {request.rm_notes}
                        </p>
                      )}
                      {request.hr_notes && (
                        <p className="text-xs bg-purple-50 p-2 rounded mb-1">
                          <span className="font-medium">HR:</span> {request.hr_notes}
                        </p>
                      )}
                      {request.admin_notes && (
                        <p className="text-xs bg-indigo-50 p-2 rounded">
                          <span className="font-medium">Admin:</span> {request.admin_notes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRequest ? 'Edit Request' : 'Create Request'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Category</Label>
              <Select value={formData.category || undefined} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.form_data?.description || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  form_data: { ...formData.form_data, description: e.target.value }
                })}
                placeholder="Enter request details"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>RM Notes</Label>
                <Input
                  value={formData.rm_notes}
                  onChange={(e) => setFormData({...formData, rm_notes: e.target.value})}
                  placeholder="Notes from Reporting Manager"
                />
              </div>
              <div>
                <Label>HR Notes</Label>
                <Input
                  value={formData.hr_notes}
                  onChange={(e) => setFormData({...formData, hr_notes: e.target.value})}
                  placeholder="Notes from HR"
                />
              </div>
              <div>
                <Label>Admin Notes</Label>
                <Input
                  value={formData.admin_notes}
                  onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
                  placeholder="Notes from Admin"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelfHelpPortalEmbed;
